import { shallowEqual } from 'utils/objectUtils';
import { GetAdvicesFunc, getExtras, getProactiveWorks } from 'modules/selfServices/utils';

import { Advice } from 'types/Checklist';
import GenericQuestion from 'types/Question';
import type ParkingForm from 'types/ParkingForm';
import { PoliciesForm } from 'types/PoliciesForm';
import type { ExtrasForm } from 'types/ExtrasForm';
import type QuestionForm from 'types/QuestionForm';
import { LoginData } from 'modules/auth/types/InitialState';
import type { RepairOrderForm } from 'types/RepairOrderForm';
import type { CustomerInfoForm } from 'types/CustomerInfoForm';
import type { MobilityFormType } from 'types/MobilityDocumentFormType';
import { KioskPreferencesForm, QuestionsForm } from 'types/QuestionsForm';
import { BreakdownServiceAuthorType, SelfService } from 'modules/selfServices/types/SelfService';

import Civilities from 'constants/civilities';

import { getLoginData } from 'modules/auth/selectors';

import { formAdapterSelectors } from './selectors';
import FormKeys, { CONCILIATABLE_FORM_KEYS } from './types/FormKeys';

type Data = { selfService: SelfService, loginData: Partial<LoginData> };

type ConciliateFunc<T> = (data: Data, cache?: Partial<T>) => Partial<T>;

const isQuestionsForm = (
  cache: Partial<KioskPreferencesForm | QuestionsForm> = {},
): cache is Partial<QuestionsForm> => FormKeys.QUESTIONS in cache;

const isKioskPreferencesForm = (
  cache: Partial<KioskPreferencesForm | QuestionsForm> = {},
): cache is Partial<KioskPreferencesForm> => FormKeys.KIOSK_USAGE in cache;

const conciliateQuestions = (
  formKey: FormKeys.KIOSK_USAGE | FormKeys.QUESTIONS,
  questions: GenericQuestion[],
  cache: Partial<KioskPreferencesForm | QuestionsForm>,
) => {
  if (!Array.isArray(questions)) {
    return undefined;
  }

  let cacheItems: GenericQuestion[] = [];
  if (isQuestionsForm(cache) && formKey === FormKeys.QUESTIONS) {
    cacheItems = cache[formKey];
  } else if (isKioskPreferencesForm(cache) && formKey === FormKeys.KIOSK_USAGE) {
    cacheItems = cache[formKey];
  }

  return {
    [formKey]: questions.map((question) => {
      const cacheItem = cacheItems?.find(({ id }) => question.id === id);
      const hasChange = cacheItem === undefined
        || cacheItem.mandatory !== question.mandatory
        || !shallowEqual(cacheItem.title as Record<string, unknown>, question.title as Record<string, unknown>)
        || !shallowEqual(cacheItem.description, question.description);

      return {
        ...question,
        comment: hasChange ? question.comment : cacheItem?.comment,
        response: hasChange ? question.response : cacheItem?.response,
      };
    }),
  };
};

const additionalCustomerConciliate: ConciliateFunc<CustomerInfoForm> = ({ selfService }, cache) => ({
  civility: Civilities.NONE,
  language: selfService.customerInfo?.language,
  ...cache,
  ...selfService.additionalCustomer?.info,
});

const customerInfoConciliate: ConciliateFunc<CustomerInfoForm> = ({ selfService, loginData }) => {
  const shouldSetDefaultPhoneNumber = selfService.breakdownServiceAuthorType === BreakdownServiceAuthorType.CUSTOMER;
  return ({
    permanent: 'true',
    civility: Civilities.NONE,
    phoneNumber: shouldSetDefaultPhoneNumber ? loginData.phoneNumber : undefined,
    ...selfService.customerInfo,
  });
};

type HasChangesFunc = (advice: Advice, cachedAdvice: Advice) => boolean;
type GetSimpleChecklistConciliateFunc = <T>(getAdvices: GetAdvicesFunc, hasChanges: HasChangesFunc, cacheKey: keyof T) => ConciliateFunc<T>;

/**
 * @description Build a conciliate function for 'simple' checklists.
 * This function checks if any of the provided advices have changed and updates the cache accordingly.
 */
const getSimpleChecklistConciliate: GetSimpleChecklistConciliateFunc = (getAdvices, hasChanges, cacheKey) => ({ selfService }, cache) => {
  const advices = getAdvices(selfService);

  const newAdvices = advices?.reduce((acc, advice) => {
    if (advice) {
      const cachedAdvices = cache?.[cacheKey] as Array<Advice>;
      const cacheItem = cachedAdvices?.find(({ adviceId }) => adviceId === advice.adviceId);

      const hasChange = hasChanges(advice, cacheItem);

      acc.push({
        ...advice,
        status: hasChange ? advice.status : cacheItem.status,
      });
    }

    return acc;
  }, []);

  return {
    ...cache,
    [cacheKey]: newAdvices,
  };
};

const extraHasChanges: HasChangesFunc = (extra, cacheItem) => cacheItem === undefined
      || cacheItem.discountedPrice !== extra.discountedPrice
      || cacheItem.mandatory !== extra.mandatory
      || !shallowEqual(cacheItem.adviceName, extra.adviceName);

const extrasConciliate: ConciliateFunc<ExtrasForm> = getSimpleChecklistConciliate(getExtras, extraHasChanges, 'extras');

const proactiveHasChanges: HasChangesFunc = (work, cacheItem) => cacheItem === undefined
      || cacheItem.discountedPrice !== work.discountedPrice
      || !shallowEqual(cacheItem.adviceName, work.adviceName);

const proactiveWorksConciliate = getSimpleChecklistConciliate(getProactiveWorks, proactiveHasChanges, 'proactiveWorks');
const repairOrderConciliate: ConciliateFunc<RepairOrderForm> = ({ selfService, ...rest }, cache) => ({
  medias: selfService.medias,
  customerComment: selfService.customerComment,
  ...cache,
  ...proactiveWorksConciliate({ selfService, ...rest }, cache),
});

const questionsConciliate: ConciliateFunc<QuestionForm> = ({ selfService }, cache) => conciliateQuestions(
  FormKeys.QUESTIONS,
  selfService.questionForm?.questions,
  cache,
);

const parkingConciliate: ConciliateFunc<ParkingForm> = ({ selfService }, cache) => ({
  ...selfService.parking,
  ...cache,
  mapId: cache?.mapId
    ?? selfService.parking?.mapId
    ?? Object.values(selfService.parkingMap?.selectedMaps ?? {})[0]
    ?? selfService.parkingMap?.maps?.[0]?.id,
});

const mobilityParkingConciliate: ConciliateFunc<ParkingForm> = ({ selfService }, cache) => ({
  ...(selfService?.mobility?.parkingReturn ?? {}),
  ...cache,
  mapId: cache?.mapId
    ?? selfService?.mobility?.parkingReturn?.mapId
    ?? Object.values(selfService.parkingMap?.selectedMaps ?? {})[0]
    ?? selfService.parkingMap?.maps?.[0]?.id,
});

const mobilityConciliate: ConciliateFunc<MobilityFormType> = ({ selfService }, cache) => ({
  ...cache,
  ...selfService?.mobility,
  documents: selfService?.mobility?.documents?.map((document) => {
    const cacheDocument = cache?.documents?.find(({ id }) => document.id === id);
    return cacheDocument?.status !== document.status ? document : { ...cacheDocument, validationStatus: document.validationStatus };;
  }),
});

const kioskPreferencesQuestionsConciliate: ConciliateFunc<KioskPreferencesForm> = ({ selfService }, cache) => conciliateQuestions(
  FormKeys.KIOSK_USAGE,
  selfService.kioskPreferencesQuestions,
  cache,
);

/**
 * @param {SelfService} selfService
 * @param {PoliciesForm} cache
 * @returns {PoliciesForm}
 */
const policiesConciliate: ConciliateFunc<PoliciesForm> = (_, cache) => ({
  basketConsent: false,
  basketGTC: false,
  ...cache,
});

const Conciliate: Record<`${string}Conciliate`, ConciliateFunc<unknown>> = {
  additionalCustomerConciliate,
  customerInfoConciliate,
  extrasConciliate,
  questionsConciliate,
  repairOrderConciliate,
  parkingConciliate,
  mobilityParkingConciliate,
  mobilityConciliate,
  kioskPreferencesQuestionsConciliate,
  policiesConciliate,
};

export default Conciliate;

/**
 * Reconciliate the combination of `selfService` and `loginData` with the provided `form` data.
 * It merges answers provided by the customer (`form` data) into existing data (from the API, most of the time).
 *
 * @see {@link Conciliate} for more information on how individual forms are merged
 */
export const reconciliateForms = (
  selfService: SelfService,
  loginData: ReturnType<typeof getLoginData>,
  form: ReturnType<typeof formAdapterSelectors.selectEntities>,
) => CONCILIATABLE_FORM_KEYS.reduce<Record<FormKeys, { key: FormKeys; values: unknown }>>((acc, formKey) => {
  const fn = Conciliate[`${formKey}Conciliate`];
  if (typeof fn === 'function') {
    return {
      ...acc,
      [formKey]: {
        key: formKey,
        values: fn({ selfService, loginData }, form[formKey]?.values),
      },
    };
  }
  return acc;
}, {} as unknown as Record<FormKeys, { key: FormKeys; values: unknown }>);
